import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { ApiService } from './api.service';
import { AuthTokenService } from './auth-token.service';
import { endpoints } from '../../../../environments/endpoints';
import { AuthResponse } from '../../../Interfaces/api/auth/auth-response.interface';
import { OperatorResponse } from '../../../Interfaces/api/backoffice/operator.interface';
import { LaravelDefaultResponse } from '../../../Interfaces/api/shared/response.interface';
import { Operator } from '../../../Models/backoffice/operator.model';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly _router = inject(Router);
  private readonly _apiService = inject(ApiService);
  private readonly _authTokenService = inject(AuthTokenService);

  private readonly _currentOperator = new BehaviorSubject<Operator | null>(null);
  readonly currentOperator$ = this._currentOperator.asObservable();

  private readonly _isAuthenticated = new BehaviorSubject<boolean>(false);
  readonly isAuthenticated$ = this._isAuthenticated.asObservable();

  private readonly _authInitialized = new BehaviorSubject<boolean>(false);
  private _initialization$?: Observable<boolean>;

  initializeAuth(): Observable<boolean> {
    if (this._authInitialized.value) {
      return of(this._isAuthenticated.value);
    }

    if (!this._initialization$) {
      this._initialization$ = this._bootstrapAuth().pipe(
        map((operator) => !!operator),
        catchError(() => of(false)),
        tap((is_authenticated) => {
          this._authInitialized.next(true);
          this._isAuthenticated.next(is_authenticated);
        }),
        shareReplay(1),
      );
    }

    return this._initialization$;
  }

  exchangeCode(code: string): Observable<Operator> {
    const request = this._apiService.post<LaravelDefaultResponse<AuthResponse>>(
      endpoints.auth.exchangeCode,
      { body: { code }, skipLoading: true, skipError: true },
    );

    return this._apiService.run<LaravelDefaultResponse<AuthResponse>>(request).pipe(
      tap((response) => this.setAuth(response.data)),
      switchMap(() => this.me()),
      tap(() => this._authInitialized.next(true)),
    );
  }

  refreshToken(): Observable<AuthResponse> {
    const refresh_token = this._authTokenService.getRefreshToken();

    if (!refresh_token) {
      return throwError(() => new Error('No refresh token available.'));
    }

    const request = this._apiService.post<LaravelDefaultResponse<AuthResponse>>(
      endpoints.auth.refresh,
      { body: { refresh_token }, skipLoading: true, skipError: true },
    );

    return this._apiService.run<LaravelDefaultResponse<AuthResponse>>(request).pipe(
      map((response) => response.data),
      tap((auth_data) => this.setAuth(auth_data)),
    );
  }

  me(): Observable<Operator> {
    const request = this._apiService.get<LaravelDefaultResponse<OperatorResponse>>(
      endpoints.backoffice.me,
      { skipLoading: true },
    );

    return this._apiService.run<LaravelDefaultResponse<OperatorResponse>>(request).pipe(
      map((response) => new Operator(response.data)),
      tap((operator) => {
        this._currentOperator.next(operator);
        this._isAuthenticated.next(true);
      }),
    );
  }

  logout(): void {
    const refresh_token = this._authTokenService.getRefreshToken();

    if (refresh_token) {
      this._apiService.runAndForget(
        this._apiService.post<unknown>(endpoints.auth.logout, {
          body: { refresh_token },
          skipError: true,
        }),
      );
    }

    this.clearAuth();
    this._router.navigate(['/auth/login']);
  }

  setAuth(auth_data: AuthResponse): void {
    this._authTokenService.setToken(auth_data);
    this._isAuthenticated.next(true);
  }

  clearAuth(): void {
    this._authTokenService.clearToken();
    this._currentOperator.next(null);
    this._isAuthenticated.next(false);
  }

  setReturnUrl(return_url: string): void {
    this._authTokenService.setReturnUrl(return_url);
  }

  getAndClearReturnUrl(): string {
    return this._authTokenService.getAndClearReturnUrl();
  }

  getCurrentOperator(): Operator | null {
    return this._currentOperator.value;
  }

  isAuthenticated(): boolean {
    return this._isAuthenticated.value && this._authTokenService.hasValidToken();
  }

  private _bootstrapAuth(): Observable<Operator | null> {
    if (this._authTokenService.hasValidToken()) {
      return this.me().pipe(catchError((error) => this._handleBootstrapError(error)));
    }

    if (this._authTokenService.hasRefreshToken()) {
      return this.refreshToken().pipe(
        switchMap(() => this.me()),
        catchError((error) => this._handleBootstrapError(error)),
      );
    }

    this.clearAuth();

    return of(null);
  }

  private _handleBootstrapError(error: unknown): Observable<null> {
    if (this._isAuthenticationError(error)) {
      this.clearAuth();
    }

    return of(null);
  }

  private _isAuthenticationError(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse)) {
      return true;
    }

    return error.status === 401 || error.status === 403;
  }
}
